/**
 * Easify Calculator Mutual Exclusion Control
 * Ensures only one calculator option group can be active at a time
 */

(function() {
  'use strict';

  function initCalculatorControl() {
    // Wait for Easify calculator to load
    const checkInterval = setInterval(() => {
      // Look for Easify calculator container
      const calculatorContainer = document.querySelector('[data-easify-product-options], .easify-product-options, #easify-product-options');
      
      if (calculatorContainer) {
        clearInterval(checkInterval);
        setupMutualExclusion(calculatorContainer);
      }
    }, 500);

    // Stop checking after 10 seconds
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  function setupMutualExclusion(container) {
    // Find all input groups - looking for the calculator sections
    // Easify typically uses specific class patterns or data attributes
    const inputGroups = findCalculatorGroups(container);
    
    if (inputGroups.length === 0) {
      console.warn('No calculator groups found. Retrying with broader selectors...');
      // Retry with more generic approach
      setTimeout(() => setupWithGenericSelectors(container), 1000);
      return;
    }

    console.log(`Found ${inputGroups.length} calculator groups`);
    
    inputGroups.forEach((group, index) => {
      const inputs = group.querySelectorAll('input[type="text"], input[type="number"]');
      
      inputs.forEach((input, inputIndex) => {
        input.addEventListener('input', function() {
          handleInputChange(inputGroups, index);
        });
        
        // Make Width fields (second input in each pair) readonly
        if (inputIndex === 1 || isWidthField(input)) {
          makeInputReadonly(input);
        }
      });
    });
  }
  
  function isWidthField(input) {
    // Check if this input is a Width field by looking at nearby labels
    const parent = input.closest('div[class*="field"], div[class*="group"], div');
    if (!parent) return false;
    
    const labelText = parent.textContent.toLowerCase();
    return labelText.includes('width') || labelText.includes('wide');
  }
  
  function makeInputReadonly(input) {
    input.setAttribute('readonly', 'readonly');
    input.style.backgroundColor = '#f5f5f5';
    input.style.cursor = 'not-allowed';
    input.style.color = '#666';
  }

  function findCalculatorGroups(container) {
    // Try multiple selector strategies to find calculator groups
    let groups = [];
    
    // Strategy 1: Look for common Easify patterns
    groups = Array.from(container.querySelectorAll('.easify-option-group, .easify-field-group, [data-option-group]'));
    
    if (groups.length > 0) return groups;
    
    // Strategy 2: Look for sections containing "sq ft" text
    const allSections = container.querySelectorAll('div[class*="option"], div[class*="field"], div[class*="group"]');
    groups = Array.from(allSections).filter(section => {
      const text = section.textContent || '';
      return text.includes('sq ft') || text.includes('to');
    });
    
    if (groups.length > 0) return groups;
    
    // Strategy 3: Find parent containers of input pairs (Length + Width)
    const allInputs = container.querySelectorAll('input[type="text"], input[type="number"]');
    const parentMap = new Map();
    
    allInputs.forEach(input => {
      // Find a suitable parent container (2-3 levels up)
      let parent = input.parentElement;
      for (let i = 0; i < 3 && parent; i++) {
        if (parent.querySelectorAll('input').length >= 2) {
          parentMap.set(parent, true);
          break;
        }
        parent = parent.parentElement;
      }
    });
    
    return Array.from(parentMap.keys());
  }

  function setupWithGenericSelectors(container) {
    // More aggressive approach - find all inputs and group by proximity
    const allInputs = Array.from(container.querySelectorAll('input[type="text"], input[type="number"]'));
    
    if (allInputs.length === 0) {
      console.error('No inputs found in calculator');
      return;
    }

    // Group inputs by their parent containers
    const groupMap = new Map();
    
    allInputs.forEach(input => {
      // Find parent that contains exactly 2 inputs (Length + Width pair)
      let parent = input.parentElement;
      for (let i = 0; i < 5 && parent; i++) {
        const inputsInParent = parent.querySelectorAll('input[type="text"], input[type="number"]');
        if (inputsInParent.length === 2) {
          if (!groupMap.has(parent)) {
            groupMap.set(parent, Array.from(inputsInParent));
          }
          break;
        }
        parent = parent.parentElement;
      }
    });

    const groups = Array.from(groupMap.keys());
    
    if (groups.length === 0) {
      console.error('Could not identify calculator groups');
      return;
    }

    console.log(`Found ${groups.length} calculator groups using generic selectors`);
    
    groups.forEach((group, index) => {
      const inputs = groupMap.get(group);
      inputs.forEach((input, inputIndex) => {
        input.addEventListener('input', function() {
          handleInputChange(groups, index);
        });
        
        // Make Width fields (second input in each pair) readonly
        if (inputIndex === 1 || isWidthField(input)) {
          makeInputReadonly(input);
        }
      });
    });
  }

  function handleInputChange(groups, activeIndex) {
    // Check ALL groups to see if any has BOTH fields filled
    let groupWithCompleteValue = -1;
    
    groups.forEach((group, index) => {
      const inputs = group.querySelectorAll('input[type="text"], input[type="number"]');
      
      // Check if ALL inputs in this group have values (both Length AND Width)
      const allFieldsFilled = inputs.length >= 2 && 
                              Array.from(inputs).every(input => input.value.trim() !== '');
      
      if (allFieldsFilled) {
        groupWithCompleteValue = index;
      }
    });
    
    // If no group has both fields filled, enable all groups
    if (groupWithCompleteValue === -1) {
      groups.forEach(group => enableGroup(group));
      return;
    }
    
    // Otherwise, disable all groups except the one with both fields filled
    groups.forEach((group, index) => {
      if (index === groupWithCompleteValue) {
        enableGroup(group);
      } else {
        disableGroup(group);
      }
    });
  }

  function disableGroup(group) {
    const inputs = group.querySelectorAll('input[type="text"], input[type="number"]');
    inputs.forEach(input => {
      input.disabled = true;
      input.style.opacity = '0.5';
      input.style.cursor = 'not-allowed';
      input.style.backgroundColor = '#f5f5f5';
    });
    
    // Also disable any associated labels or containers
    group.style.opacity = '0.6';
  }

  function enableGroup(group) {
    const inputs = group.querySelectorAll('input[type="text"], input[type="number"]');
    inputs.forEach(input => {
      input.disabled = false;
      input.style.opacity = '1';
      input.style.cursor = '';
      input.style.backgroundColor = '';
    });
    
    group.style.opacity = '1';
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCalculatorControl);
  } else {
    initCalculatorControl();
  }

  // Also listen for dynamic content changes (in case calculator loads via AJAX)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              const calculator = node.querySelector ? node.querySelector('[data-easify-product-options], .easify-product-options, #easify-product-options') : null;
              if (calculator) {
                setupMutualExclusion(calculator);
              }
            }
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Add to Cart Button Validation
  function initCartValidation() {
    const checkInterval = setInterval(() => {
      const addToCartButton = document.querySelector('button[name="add"]');
      const easifyContainer = document.querySelector('[data-easify-product-options], .easify-product-options, #easify-product-options');
      
      if (addToCartButton && easifyContainer) {
        clearInterval(checkInterval);
        setupCartValidation(addToCartButton, easifyContainer);
      }
    }, 500);

    setTimeout(() => clearInterval(checkInterval), 15000);
  }

  function setupCartValidation(addToCartButton, easifyContainer) {
    disableAddToCart(addToCartButton);
    
    const requiredFields = identifyRequiredFields(easifyContainer);
    
    if (requiredFields.length === 0) {
      enableAddToCart(addToCartButton);
      return;
    }
    
    requiredFields.forEach(field => {
      field.addEventListener('input', () => validateCartFields(requiredFields, addToCartButton));
      field.addEventListener('change', () => validateCartFields(requiredFields, addToCartButton));
    });
    
    validateCartFields(requiredFields, addToCartButton);
  }

  function identifyRequiredFields(container) {
    const fields = [];
    const allInputs = container.querySelectorAll('input[type="text"], input[type="number"]');
    
    allInputs.forEach(input => {
      if (input.disabled || input.readOnly) return;
      
      const parent = input.closest('div[class*="field"], div[class*="group"], div[class*="option"], .easify-option, .easify-field');
      if (!parent) return;
      
      const labelText = parent.textContent.toLowerCase();
      const inputName = (input.name || '').toLowerCase();
      const inputId = (input.id || '').toLowerCase();
      const placeholder = (input.placeholder || '').toLowerCase();
      
      const isLength = labelText.includes('length') || inputName.includes('length') || 
                      inputId.includes('length') || placeholder.includes('length');
      const isWidth = labelText.includes('width') || inputName.includes('width') || 
                     inputId.includes('width') || placeholder.includes('width');
      const isHeight = labelText.includes('height') || inputName.includes('height') || 
                      inputId.includes('height') || placeholder.includes('height');
      
      if (isLength || isWidth || isHeight) {
        fields.push(input);
      }
    });
    
    return fields;
  }

  function validateCartFields(requiredFields, addToCartButton) {
    const allFilled = requiredFields.every(field => {
      const value = field.value.trim();
      return value !== '' && value !== '0';
    });
    
    if (allFilled) {
      enableAddToCart(addToCartButton);
    } else {
      disableAddToCart(addToCartButton);
    }
  }

  function disableAddToCart(button) {
    if (!button) return;
    
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.style.opacity = '0.5';
    button.style.cursor = 'not-allowed';
    
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent;
    }
    
    button.textContent = 'Enter Length, Width & Height';
  }

  function enableAddToCart(button) {
    if (!button) return;
    
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.style.opacity = '';
    button.style.cursor = '';
    
    if (button.dataset.originalText) {
      button.textContent = button.dataset.originalText;
    }
  }

  // Initialize cart validation
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartValidation);
  } else {
    initCartValidation();
  }


})();
